import { useEffect } from 'react';

import { params } from '@aline/routes';
import { useScrollToLocationForReference } from 'src/editor';
import { useConsumePendingParam } from 'src/helpers';
import { useMaybeEditor } from 'src/layout-engine';
import { useSnapshot } from 'valtio';

import { $chatThreadState } from '../../chat-state';
import type { AIChatItem } from '../helpers/aiChatItem';
import { createAIChatItem, parseAIChatItem } from '../helpers/aiChatItem';
import { parseLocation } from '../helpers/parseLocation';

/**
 * Scroll-only effect: watches $chatThreadState.autoScrollAIChatItem and scrolls
 * both the chat panel and (for citations) the document canvas once the DOM is ready.
 * Safe to use in the global drawer (no React Router param dependencies).
 */
export function useScrollToChatItemEffect() {
  const { autoScrollAIChatItem } = useSnapshot($chatThreadState);
  const editor = useMaybeEditor();
  const { scrollToLocationForReference } = useScrollToLocationForReference();

  useEffect(() => {
    if (!autoScrollAIChatItem) return;

    /**
     * For citations we also need to scroll the document canvas, which requires
     * the editor to be initialized. Wait until it is ready so the scroll
     * is not lost due to a stale closure capturing a null editor.
     */
    if (autoScrollAIChatItem.type === 'citation' && !editor) return;

    /**
     * We need to give enough time for the DOM to render the specific chat item we're trying to scroll to.
     * This is especially important for large documents with many messages. 2.5s is good and not too slow for a user.
     */
    setTimeout(() => {
      const element = document.getElementById(createAIChatItem(autoScrollAIChatItem));
      if (!element) return;

      // Do not use smooth-scroll-into-view-if-needed here, it causes a bug in which the scroll locks up.
      element.scrollIntoView({
        behavior: 'smooth',
        block: autoScrollAIChatItem.type === 'message' ? 'start' : 'center',
      });

      // For citations, also scroll the document canvas to the cited paragraph.
      if (autoScrollAIChatItem.type === 'citation' && editor) {
        const citationHref = element.getAttribute('data-citation-href');
        if (citationHref) {
          const referencedText = element.getAttribute('data-referenced-text') ?? '';
          const aiReference = parseLocation(citationHref, referencedText, editor);
          if (aiReference) scrollToLocationForReference({ aiReference });
        }
      }

      $chatThreadState.autoScrollAIChatItem = null;
    }, 2500);
  }, [autoScrollAIChatItem, editor, scrollToLocationForReference]);
}

export function useAIChatItemParam(onAIChatItem: (aiChatItem: AIChatItem) => void) {
  const [aiChatItemArrival, acknowledgeArrival] = useConsumePendingParam(params.aiChatItem);

  useEffect(() => {
    if (!aiChatItemArrival) return;

    acknowledgeArrival();
    const aiChatItem = parseAIChatItem(aiChatItemArrival.value);
    if (!aiChatItem) return;

    onAIChatItem(aiChatItem);
  }, [acknowledgeArrival, aiChatItemArrival, onAIChatItem]);
}
